// Supabase data access for the seating planner.
//
// Field-name conventions between the DB and the client-side state:
//   DB column                  →  client field
//   people.name                →  Name        (capitalised for CSV compat)
//   groups.host_person_id      →  host        (translated in updateGroup)
//   activities.default_group_size → defaultGroupSize
//
// All mutations throw on error; callers decide how to surface it.
// IDs are UUIDs generated client-side so state can be updated
// optimistically without waiting for a round-trip.

import { supabase } from "./supabase.js";

// ---------- helpers ----------
const uid = () =>
  (globalThis.crypto && globalThis.crypto.randomUUID)
    ? globalThis.crypto.randomUUID()
    : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });

function throwIf(error, label) {
  if (error) {
    console.error(`[store] ${label} failed:`, error);
    throw error;
  }
}

// ---------- events ----------
export async function listEvents() {
  const { data, error } = await supabase
    .from("events")
    .select("id, name, created_at")
    .order("created_at", { ascending: true });
  throwIf(error, "listEvents");
  return data || [];
}

export async function createEvent(name) {
  const id = uid();
  const { data, error } = await supabase
    .from("events")
    .insert({ id, name })
    .select("id, name, created_at")
    .single();
  throwIf(error, "createEvent");
  return data;
}

export async function renameEvent(id, name) {
  const { error } = await supabase.from("events").update({ name }).eq("id", id);
  throwIf(error, "renameEvent");
}

export async function deleteEvent(id) {
  const { error } = await supabase.from("events").delete().eq("id", id);
  throwIf(error, "deleteEvent");
}

// ---------- load event graph ----------
// Returns { people: [{id, Name, ...extra}], activities: [{id, name, type, groups: [{id, name, capacity, host, people:[pid]}]}] }
export async function loadEvent(eventId) {
  const [peopleRes, activitiesRes] = await Promise.all([
    supabase.from("people").select("*").eq("event_id", eventId).order("position"),
    supabase.from("activities").select("*").eq("event_id", eventId).order("position"),
  ]);
  throwIf(peopleRes.error, "loadEvent.people");
  throwIf(activitiesRes.error, "loadEvent.activities");

  const people = (peopleRes.data || []).map(p => ({
    id: p.id,
    Name: p.name,
    ...(p.extra || {}),
  }));

  const activityIds = (activitiesRes.data || []).map(a => a.id);
  let groups = [];
  if (activityIds.length) {
    const { data, error } = await supabase
      .from("groups")
      .select("*")
      .in("activity_id", activityIds)
      .order("position");
    throwIf(error, "loadEvent.groups");
    groups = data || [];
  }

  const groupIds = groups.map(g => g.id);
  let members = [];
  if (groupIds.length) {
    const { data, error } = await supabase
      .from("group_members")
      .select("*")
      .in("group_id", groupIds)
      .order("position");
    throwIf(error, "loadEvent.members");
    members = data || [];
  }

  const membersByGroup = {};
  for (const m of members) {
    (membersByGroup[m.group_id] ||= []).push(m.person_id);
  }
  const groupsByActivity = {};
  for (const g of groups) {
    (groupsByActivity[g.activity_id] ||= []).push({
      id: g.id,
      name: g.name,
      capacity: g.capacity,
      host: g.host_person_id,
      people: membersByGroup[g.id] || [],
    });
  }
  const activities = (activitiesRes.data || []).map(a => ({
    id: a.id,
    name: a.name,
    type: a.type,
    defaultGroupSize: a.default_group_size ?? null,
    groups: groupsByActivity[a.id] || [],
  }));

  return { people, activities };
}

// ---------- people ----------
export async function addPeople(eventId, rows, startPosition = 0) {
  // rows: [{Name, ...extra}]
  const payload = rows.map((r, i) => {
    const { Name, id: _ignore, ...rest } = r;
    return {
      id: uid(),
      event_id: eventId,
      name: Name,
      extra: rest,
      position: startPosition + i,
    };
  });
  const { data, error } = await supabase.from("people").insert(payload).select();
  throwIf(error, "addPeople");
  return (data || []).map(p => ({ id: p.id, Name: p.name, ...(p.extra || {}) }));
}

export async function deletePerson(id) {
  const { error } = await supabase.from("people").delete().eq("id", id);
  throwIf(error, "deletePerson");
}

// ---------- activities ----------
export async function addActivity(eventId, { name, type, position, defaultGroupSize }) {
  const id = uid();
  const { error } = await supabase
    .from("activities")
    .insert({ id, event_id: eventId, name, type, position, default_group_size: defaultGroupSize ?? null });
  throwIf(error, "addActivity");
  return id;
}

export async function updateActivity(id, patch) {
  const dbPatch = { ...patch };
  if ("defaultGroupSize" in dbPatch) {
    dbPatch.default_group_size = dbPatch.defaultGroupSize;
    delete dbPatch.defaultGroupSize;
  }
  const { error } = await supabase.from("activities").update(dbPatch).eq("id", id);
  throwIf(error, "updateActivity");
}

export async function deleteActivity(id) {
  const { error } = await supabase.from("activities").delete().eq("id", id);
  throwIf(error, "deleteActivity");
}

// ---------- groups ----------
export async function addGroup(activityId, { name, capacity, position }) {
  const id = uid();
  const { error } = await supabase
    .from("groups")
    .insert({ id, activity_id: activityId, name, capacity, position });
  throwIf(error, "addGroup");
  return id;
}

export async function updateGroup(id, patch) {
  const dbPatch = { ...patch };
  if ("host" in dbPatch) {
    dbPatch.host_person_id = dbPatch.host;
    delete dbPatch.host;
  }
  const { error } = await supabase.from("groups").update(dbPatch).eq("id", id);
  throwIf(error, "updateGroup");
}

export async function deleteGroup(id) {
  const { error } = await supabase.from("groups").delete().eq("id", id);
  throwIf(error, "deleteGroup");
}

// ---------- group membership ----------
// Replace the full ordered membership of a group.
export async function setGroupMembers(groupId, personIds) {
  const { error: delErr } = await supabase
    .from("group_members")
    .delete()
    .eq("group_id", groupId);
  throwIf(delErr, "setGroupMembers.delete");
  if (!personIds.length) return;
  const rows = personIds.map((pid, i) => ({
    group_id: groupId,
    person_id: pid,
    position: i,
  }));
  const { error: insErr } = await supabase.from("group_members").insert(rows);
  throwIf(insErr, "setGroupMembers.insert");
}

// ---------- rules ----------
export async function listRules(eventId) {
  const { data, error } = await supabase
    .from("rules")
    .select("*")
    .eq("event_id", eventId)
    .order("created_at", { ascending: true });
  throwIf(error, "listRules");
  return data || [];
}

export async function createRule(eventId, { personA, personB, type, count, activityTypes }) {
  const id = uid();
  const { data, error } = await supabase
    .from("rules")
    .insert({
      id,
      event_id: eventId,
      person_a: personA,
      person_b: personB,
      type,
      count: count ?? null,
      activity_types: activityTypes && activityTypes.length ? activityTypes : null,
    })
    .select()
    .single();
  throwIf(error, "createRule");
  return data;
}

export async function deleteRule(id) {
  const { error } = await supabase.from("rules").delete().eq("id", id);
  throwIf(error, "deleteRule");
}
